import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Alert,
  Button
} from '@mui/material';
import { filterProgramExamAssessments, collectAddressedIacCodesFromAssessments } from '../utils/taskAssessmentIacAudit';

function expectedCodesFromConfig(row) {
  const cfg = row && row.config;
  const fromConfig =
    cfg && typeof cfg === 'object' && Array.isArray(cfg.selected_iac_codes)
      ? cfg.selected_iac_codes.map(String).filter(Boolean)
      : [];
  return [...new Set(fromConfig)].sort();
}

export default function ProgramExamAuditPanel({ assessments }) {
  const programExams = useMemo(
    () => filterProgramExamAssessments(assessments || []),
    [assessments]
  );

  const rows = useMemo(() => {
    return programExams.map((exam) => {
      const expected = expectedCodesFromConfig(exam);
      const addressed = collectAddressedIacCodesFromAssessments([exam]);
      const addrSet = new Set(addressed);
      const missing = expected.filter((c) => !addrSet.has(c));
      return { exam, expected, addressed, missing };
    });
  }, [programExams]);

  const downloadCsv = () => {
    const lines = [['Exam id', 'Kind', 'Expected IACs (config)', 'Missing in payload', 'Status']];
    for (const { exam, expected, missing } of rows) {
      lines.push([
        String(exam.id),
        exam.kind || '',
        expected.join('; '),
        missing.join('; '),
        exam.status || ''
      ]);
    }
    const blob = new Blob([lines.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'program-exam-iac-audit.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (programExams.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No program exams yet. Create one with &quot;New program exam&quot;.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        IAC audit (program exams)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Expected codes come from the IACs you selected when generating each exam (
        <code>config.selected_iac_codes</code>). The table lists any of those codes not present in the
        question payloads&apos; <code>iac_codes</code> unions.
      </Typography>
      <Stack direction="row" sx={{ mb: 2 }}>
        <Button size="small" variant="outlined" onClick={downloadCsv}>
          Export CSV
        </Button>
      </Stack>

      {rows.some((r) => r.missing.length > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some exams are missing required IACs in the stored questions. Edit the exam or regenerate.
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Expected IACs</TableCell>
            <TableCell>Missing</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ exam, expected, missing }) => (
            <TableRow key={exam.id}>
              <TableCell>{exam.id}</TableCell>
              <TableCell>{exam.kind === 'mcq' ? 'MCQ' : 'Free text'}</TableCell>
              <TableCell>
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {expected.map((c) => (
                    <Chip key={c} size="small" label={c} variant="outlined" />
                  ))}
                </Stack>
              </TableCell>
              <TableCell>
                {missing.length === 0 ? (
                  <Chip size="small" label="OK" color="success" />
                ) : (
                  <Stack direction="row" flexWrap="wrap" gap={0.5}>
                    {missing.map((c) => (
                      <Chip key={c} size="small" label={c} color="warning" />
                    ))}
                  </Stack>
                )}
              </TableCell>
              <TableCell>
                <Chip size="small" label={exam.status || ''} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
